import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { ResolvedMeeting } from '../../../connection/health';
import { MeetingConfigResult } from '../../../hooks/health/use-meeting-config';
import { diffFeatureFlags, reportedSettings } from '../meeting-config';
import { Probe, ProbeStatus } from '../types';
import HealthPanel from '../health-panel';

interface Props {
	meeting: ResolvedMeeting | null;
	config: MeetingConfigResult;
}

/** A room behaving unlike every other room is usually a flag override. */
export default function MeetingConfigPanel({ meeting, config }: Props) {
	const [showAll, setShowAll] = useState(false);

	const flags = useMemo(
		() => diffFeatureFlags(config.effective?.body, config.defaults?.body),
		[config.effective, config.defaults],
	);
	const settings = useMemo(() => reportedSettings(meeting?.settings), [meeting?.settings]);

	const overridden = flags.filter((flag) => flag.overridden);
	const visible = showAll ? flags : overridden;

	// Answers independently of sync, so it carries its own probe status.
	const probe: Probe<unknown> | undefined = config.effective
		? {
			status: config.effective.httpStatus === 200 ? ProbeStatus.Ok : ProbeStatus.Error,
			latencyMs: config.effective.latencyMs,
			httpStatus: config.effective.httpStatus,
			message: config.effective.error,
			data: null,
		}
		: undefined;

	return (
		<HealthPanel title='Meeting config' source='platform api' probe={probe}>
			{!settings.length ? (
				<p className='health-panel__note'>This room has no saved settings yet.</p>
			) : (
				<div className='health-chips'>
					{settings.map((setting) => (
						<span
							key={setting.label}
							className={classNames('health-chip', {
								'health-chip--on': setting.value === 'on',
								'health-chip--off': setting.value === 'off',
							})}
						>
							{setting.label}
							<span className='health-chip__value'>{setting.value}</span>
						</span>
					))}
				</div>
			)}

			{!flags.length && <p className='health-panel__note'>No feature flags reported.</p>}

			{!!flags.length && (
				<>
					<div className='health-panel__subhead'>
						<span>Feature flags</span>
						<span className='health-subtle'>
							{flags.filter((flag) => flag.enabled).length} of {flags.length} on
							{!!overridden.length && ` · ${overridden.length} overridden`}
						</span>
						<button
							type='button'
							className='health-link-button'
							onClick={() => setShowAll((previous) => !previous)}
						>
							{showAll ? 'Overrides only' : `All ${flags.length}`}
						</button>
					</div>

					{!visible.length ? (
						<p className='health-panel__note'>No flag differs from the platform default.</p>
					) : (
						<div className='health-chips'>
							{visible.map((flag) => (
								<span
									key={flag.name}
									className={classNames('health-chip health-chip--mono', {
										'health-chip--on': flag.enabled,
										'health-chip--off': !flag.enabled,
										'health-chip--overridden': flag.overridden,
									})}
								>
									{flag.name}
									<span className='health-chip__value'>{flag.enabled ? 'on' : 'off'}</span>
									{flag.overridden && (
										<span className='health-chip__note'>
											{flag.defaultValue === undefined
												? 'room only'
												: `default ${flag.defaultValue ? 'on' : 'off'}`}
										</span>
									)}
								</span>
							))}
						</div>
					)}
				</>
			)}
		</HealthPanel>
	);
}
